import { useState, useEffect, useRef } from 'react';
import { X, Save, AlertTriangle, Lock, History, ChevronDown, ChevronUp, Upload, FileText, Trash2, Plus } from 'lucide-react';
import { api } from '@/utils/api';
import { toast } from 'sonner';

const PROPERTY_TYPES = [
  'Residential Under Construction',
  'Residential Ready to Move',
  'Commercial Under Construction',
  'Commercial Ready to Move',
  'Land / Plot',
  'Mixed Use'
];

const DEFAULT_ADDRESSES = [
  { id: 'default', label: 'Per Annum HQ — 123 Business Park, Sector 44, Gurgaon, Haryana 122003' },
  { id: 'branch', label: 'Per Annum Branch — 456 Tower, Connaught Place, New Delhi 110001' },
];

export default function AdminUnitEditModal({ unit, onClose, onSaved }) {
  const [activeSection, setActiveSection] = useState('identifiers');
  const [formData, setFormData] = useState({});
  const [paymentPlan, setPaymentPlan] = useState([]);
  const [documents, setDocuments] = useState({ allotmentLetter: null, bba: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editReason, setEditReason] = useState('');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [hasInvestors, setHasInvestors] = useState(false);
  const allotmentRef = useRef(null);
  const bbaRef = useRef(null);

  const currentState = unit?.lifecycle?.currentState || 'DRAFT';
  const isEditable = currentState === 'DRAFT' || currentState === 'OPEN';

  const sections = [
    { id: 'identifiers', label: 'Unit Identity' },
    { id: 'llp', label: 'LLP Details' },
    { id: 'designated-partners', label: 'Designated Partners' },
    { id: 'financial', label: 'Financial' },
    { id: 'documents', label: 'Documents' },
    { id: 'agreement', label: 'Agreement Terms' },
    { id: 'payment-plan', label: 'Payment Schedule' },
  ];

  useEffect(() => {
    if (!unit) return;
    setFormData({
      unitName: unit.unitName || '',
      unitNumber: unit.unitNumber || '',
      tower: unit.tower || '',
      floor: unit.floor || '',
      configuration: unit.configuration || unit.specifications?.configuration?.split(',')[0] || '',
      propertyType: unit.propertyType || 'Residential Under Construction',
      superArea: unit.superArea || '',
      carpetArea: unit.carpetArea || '',
      llpName: unit.llp?.name || '',
      llpin: unit.llp?.llpin || '',
      llpEffectiveDate: unit.llp?.effectiveDate || '',
      llpRegisteredOffice: unit.llp?.registeredOffice || DEFAULT_ADDRESSES[0].label,
      llpRegisteredOfficeType: unit.llp?.registeredOfficeType || 'default',
      llpBankAccountName: unit.llp?.bankAccount?.accountName || '',
      llpBankName: unit.llp?.bankAccount?.bankName || '',
      llpIfsc: unit.llp?.bankAccount?.ifsc || '',
      llpAccountNumber: unit.llp?.bankAccount?.accountNumber || '',
      dp1Name: unit.designatedPartners?.dp1?.name || '',
      dp1Din: unit.designatedPartners?.dp1?.din || '',
      dp2Name: unit.designatedPartners?.dp2?.name || '',
      dp2Din: unit.designatedPartners?.dp2?.din || '',
      unitValue: unit.financial?.unitValue || '',
      capitalTarget: unit.financial?.capitalTarget || 0,
      minInvestment: unit.financial?.minInvestment || 500000,
      maxInvestment: unit.financial?.maxInvestment || 10000000,
      bbaDate: unit.bbaDate || '',
      holdingPeriod: unit.agreementTerms?.holdingPeriod || '6 Months',
      targetAppreciation: unit.agreementTerms?.targetAppreciation || '',
    });
    setPaymentPlan(unit.paymentPlan?.length ? unit.paymentPlan.map(p => ({ ...p })) : [
      { slotNumber: 1, percentage: 25, milestone: 'On Booking', description: '' },
    ]);
    setDocuments({
      allotmentLetter: unit.documents?.allotmentLetter || null,
      bba: unit.documents?.bba || null,
    });
    checkInvestors(unit.unitId);
    fetchHistory(unit.unitId);
  }, [unit]);

  const checkInvestors = async (unitId) => {
    try {
      const r = await api.get(`/units/${unitId}/lifecycle`);
      setHasInvestors((r.data?.investments || []).length > 0);
    } catch (e) { /* ignore */ }
  };

  const fetchHistory = async (unitId) => {
    try {
      const r = await api.get(`/admin/units/${unitId}/history`);
      setHistory(r.data?.history || []);
    } catch (e) { /* ignore */ }
  };

  const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const ppTotal = paymentPlan.reduce((s, p) => s + (parseInt(p.percentage) || 0), 0);
  const addPaymentSlot = () => {
    if (paymentPlan.length >= 5) { toast.error('Maximum 5 milestones allowed'); return; }
    setPaymentPlan([...paymentPlan, { slotNumber: paymentPlan.length + 1, percentage: 0, milestone: '', description: '' }]);
  };
  const removePaymentSlot = (idx) => {
    if (paymentPlan.length <= 1) { toast.error('At least one milestone required'); return; }
    setPaymentPlan(paymentPlan.filter((_, i) => i !== idx));
  };
  const updatePaymentSlot = (idx, field, value) => {
    setPaymentPlan(paymentPlan.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const handleFileUpload = (type, file) => {
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error('Only PDF files accepted'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10MB'); return; }
    setDocuments(prev => ({ ...prev, [type]: file }));
    toast.success(`${type === 'allotmentLetter' ? 'Allotment Letter' : 'BBA'} uploaded`);
  };
  const removeFile = (type) => setDocuments(prev => ({ ...prev, [type]: null }));

  const handleOfficeChange = (value) => {
    if (value === 'custom') {
      update('llpRegisteredOfficeType', 'custom');
      update('llpRegisteredOffice', '');
    } else {
      const addr = DEFAULT_ADDRESSES.find(a => a.id === value);
      update('llpRegisteredOfficeType', value);
      update('llpRegisteredOffice', addr ? addr.label : '');
    }
  };

  const isFieldLocked = (field) => {
    const investorLocked = ['unitName', 'unitNumber', 'capitalTarget', 'unitValue', 'minInvestment', 'maxInvestment', 'superArea', 'carpetArea', 'llpName'];
    if (hasInvestors && investorLocked.includes(field)) return true;
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editReason.trim()) { setError('Please provide a reason for this edit (required for audit trail)'); return; }
    if (ppTotal !== 100) { setError(`Payment schedule must total 100% (currently ${ppTotal}%)`); return; }

    setLoading(true);
    setError(null);
    try {
      await api.put(`/admin/units/${unit.unitId}`, { ...formData, paymentPlan, editReason, editedBy: 'admin' });
      toast.success('Unit updated successfully');
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update unit');
    } finally {
      setLoading(false);
    }
  };

  if (!unit) return null;

  const inputCls = "w-full bg-[#030303] border border-white/10 px-4 py-3 text-white text-sm focus:border-white/30 focus:outline-none transition-colors";
  const lockedCls = "w-full bg-[#030303] border border-white/5 px-4 py-3 text-white/30 text-sm cursor-not-allowed";
  const labelCls = "text-xs text-gray-500 uppercase tracking-wider mb-2 block";

  const renderField = (label, field, opts = {}) => {
    const locked = !isEditable || isFieldLocked(field);
    return (
      <div className={opts.colSpan2 ? 'col-span-2' : ''}>
        <label className={labelCls}>
          {label} {opts.required && <span className="text-red-400">*</span>}
          {locked && isFieldLocked(field) && <Lock className="w-3 h-3 inline ml-1 text-yellow-500" />}
        </label>
        {opts.type === 'select' ? (
          <select value={formData[field] || ''} onChange={e => update(field, e.target.value)} disabled={locked} className={locked ? lockedCls : inputCls} data-testid={`edit-${field}`}>
            {opts.options.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <input
            type={opts.type || 'text'}
            value={formData[field] || ''}
            onChange={e => update(field, e.target.value)}
            disabled={locked}
            className={`${locked ? lockedCls : inputCls} ${opts.mono ? 'font-mono' : ''}`}
            placeholder={opts.placeholder || ''}
            data-testid={`edit-${field}`}
          />
        )}
        {opts.hint && <p className="text-xs text-gray-600 mt-1">{opts.hint}</p>}
        {locked && isFieldLocked(field) && <p className="text-xs text-yellow-500/70 mt-1">Locked — unit has investors</p>}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="unit-edit-modal">
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-white/10 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-light text-white">Edit Unit</h2>
            <p className="text-sm text-gray-500 mt-0.5">{unit.unitName} — {unit.unitId}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 text-xs font-mono border ${
              currentState === 'DRAFT' ? 'border-gray-500 text-gray-400 bg-gray-500/10' :
              currentState === 'OPEN' ? 'border-[#4ADE80] text-[#4ADE80] bg-[#4ADE80]/10' :
              currentState === 'FUNDED' ? 'border-purple-500 text-purple-400 bg-purple-500/10' :
              'border-yellow-500 text-yellow-400 bg-yellow-500/10'
            }`}>{currentState}</span>
            <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="close-edit-modal"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {/* Warnings */}
        {!isEditable && (
          <div className="p-3 bg-red-500/10 border-b border-red-500/20 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <p className="text-sm text-red-400">Unit is in <strong>{currentState}</strong> state — read only.</p>
          </div>
        )}
        {hasInvestors && isEditable && (
          <div className="p-3 bg-yellow-500/10 border-b border-yellow-500/20 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
            <p className="text-sm text-yellow-400">Unit has investors — some fields are locked.</p>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-white/10 flex overflow-x-auto flex-shrink-0">
          {sections.map(s => (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className={`px-4 py-3 text-xs uppercase tracking-wider whitespace-nowrap transition-colors ${
                activeSection === s.id ? 'text-white border-b-2 border-[#4ADE80]' : 'text-gray-500 hover:text-gray-300'
              }`} data-testid={`edit-tab-${s.id}`}>
              {s.label}
            </button>
          ))}
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {/* Unit Identity */}
          {activeSection === 'identifiers' && (
            <div className="space-y-6" data-testid="edit-section-identifiers">
              <h3 className="text-sm text-white font-medium">Unit Identity & Physical Details</h3>
              <div className="grid grid-cols-2 gap-5">
                {renderField('Unit Display Name', 'unitName', { required: true })}
                {renderField('Unit Number', 'unitNumber', { required: true, hint: 'Used in Zoho LLP agreement' })}
                {renderField('Tower / Block', 'tower', { required: true })}
                {renderField('Floor', 'floor', { required: true })}
                {renderField('Configuration', 'configuration', { required: true, placeholder: '3 BHK' })}
                {renderField('Property Type', 'propertyType', { type: 'select', options: PROPERTY_TYPES })}
                {renderField('Super Built-Up Area (sq ft)', 'superArea', { type: 'number', required: true })}
                {renderField('Carpet Area (sq ft)', 'carpetArea', { type: 'number' })}
              </div>
            </div>
          )}

          {/* LLP Details */}
          {activeSection === 'llp' && (
            <div className="space-y-6" data-testid="edit-section-llp">
              <div>
                <h3 className="text-sm text-white font-medium">LLP Details</h3>
                <p className="text-xs text-gray-500 mt-1">Each unit has its own LLP entity.</p>
              </div>
              <div className="grid grid-cols-2 gap-5">
                {renderField('LLP Name', 'llpName', { required: true, colSpan2: true })}
                {renderField('LLPIN', 'llpin', { mono: true, hint: 'LLP Identification Number from MCA' })}
                {renderField('LLP Effective Date', 'llpEffectiveDate', { type: 'date' })}
                <div className="col-span-2">
                  <label className={labelCls}>LLP Registered Office</label>
                  <select value={formData.llpRegisteredOfficeType || 'default'} onChange={e => handleOfficeChange(e.target.value)}
                    className={`${inputCls} mb-2`} disabled={!isEditable} data-testid="edit-llpOffice">
                    {DEFAULT_ADDRESSES.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
                    <option value="custom">+ Add New Address</option>
                  </select>
                  {formData.llpRegisteredOfficeType === 'custom' && (
                    <input value={formData.llpRegisteredOffice || ''} onChange={e => update('llpRegisteredOffice', e.target.value)}
                      className={inputCls} disabled={!isEditable} placeholder="Enter full address" data-testid="edit-llpOfficeCustom" />
                  )}
                </div>
                <div className="col-span-2 border-t border-white/10 pt-4 mt-1">
                  <h4 className="text-xs text-gray-400 mb-3">LLP Bank Account</h4>
                </div>
                {renderField('Account Name', 'llpBankAccountName')}
                {renderField('Bank Name', 'llpBankName')}
                {renderField('IFSC Code', 'llpIfsc', { mono: true })}
                {renderField('Account Number', 'llpAccountNumber', { mono: true })}
              </div>
            </div>
          )}

          {/* Designated Partners */}
          {activeSection === 'designated-partners' && (
            <div className="space-y-6" data-testid="edit-section-dp">
              <div>
                <h3 className="text-sm text-white font-medium">Designated Partners</h3>
                <p className="text-xs text-gray-500 mt-1">Two designated partners required per LLP.</p>
              </div>
              <div className="border border-white/10 p-5 bg-white/[0.02]">
                <h4 className="text-sm text-[#4ADE80] uppercase tracking-wider mb-4">Designated Partner 1</h4>
                <div className="grid grid-cols-2 gap-5">
                  {renderField('Full Name', 'dp1Name', { required: true })}
                  {renderField('DIN / DPIN', 'dp1Din', { mono: true, hint: 'Director Identification Number from MCA' })}
                </div>
              </div>
              <div className="border border-white/10 p-5 bg-white/[0.02]">
                <h4 className="text-sm text-[#4ADE80] uppercase tracking-wider mb-4">Designated Partner 2</h4>
                <div className="grid grid-cols-2 gap-5">
                  {renderField('Full Name', 'dp2Name', { required: true })}
                  {renderField('DIN / DPIN', 'dp2Din', { mono: true, hint: 'Director Identification Number from MCA' })}
                </div>
              </div>
            </div>
          )}

          {/* Financial */}
          {activeSection === 'financial' && (
            <div className="space-y-6" data-testid="edit-section-financial">
              <h3 className="text-sm text-white font-medium">Financial Details</h3>
              <div className="grid grid-cols-2 gap-5">
                {renderField('Unit Purchase Price (INR)', 'unitValue', { type: 'number', required: true, mono: true, hint: 'Total cost as per BBA' })}
                {renderField('Capital Target (INR)', 'capitalTarget', { type: 'number', required: true, mono: true, hint: 'Amount to raise from investors' })}
                {renderField('Min Capital Contribution (INR)', 'minInvestment', { type: 'number', mono: true, hint: 'Per-unit minimum. Overrides platform default.' })}
                {renderField('Max Investment (INR)', 'maxInvestment', { type: 'number', mono: true })}
              </div>
            </div>
          )}

          {/* Documents */}
          {activeSection === 'documents' && (
            <div className="space-y-6" data-testid="edit-section-documents">
              <div>
                <h3 className="text-sm text-white font-medium">Unit Documents</h3>
                <p className="text-xs text-gray-500 mt-1">PDF format, max 10MB each.</p>
              </div>
              {/* Allotment Letter */}
              <div className="border border-white/10 p-5 bg-white/[0.02]">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-sm text-white">Allotment Letter</h4>
                    <p className="text-xs text-gray-500 mt-0.5">Builder's allotment letter for this unit</p>
                  </div>
                  {documents.allotmentLetter && (
                    <button type="button" onClick={() => removeFile('allotmentLetter')} className="p-1 text-gray-500 hover:text-red-400" data-testid="edit-remove-allotment">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {documents.allotmentLetter ? (
                  <div className="flex items-center gap-3 p-3 bg-[#4ADE80]/10 border border-[#4ADE80]/20">
                    <FileText className="w-5 h-5 text-[#4ADE80]" />
                    <div className="flex-1">
                      <p className="text-sm text-white">{documents.allotmentLetter.name}</p>
                      <p className="text-xs text-gray-500">{(documents.allotmentLetter.size / 1024).toFixed(0)} KB</p>
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => allotmentRef.current?.click()} disabled={!isEditable}
                    className="w-full flex items-center justify-center gap-2 p-4 border border-dashed border-white/20 text-gray-400 hover:text-white hover:border-white/40 transition-colors"
                    data-testid="edit-upload-allotment">
                    <Upload className="w-4 h-4" /><span className="text-sm">Upload Allotment Letter (PDF)</span>
                  </button>
                )}
                <input ref={allotmentRef} type="file" accept=".pdf" className="hidden" onChange={e => handleFileUpload('allotmentLetter', e.target.files[0])} />
              </div>
              {/* BBA */}
              <div className="border border-white/10 p-5 bg-white/[0.02]">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-sm text-white">Builder-Buyer Agreement (BBA)</h4>
                    <p className="text-xs text-gray-500 mt-0.5">Executed BBA between builder and the LLP</p>
                  </div>
                  {documents.bba && (
                    <button type="button" onClick={() => removeFile('bba')} className="p-1 text-gray-500 hover:text-red-400" data-testid="edit-remove-bba">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {documents.bba ? (
                  <div className="flex items-center gap-3 p-3 bg-[#4ADE80]/10 border border-[#4ADE80]/20">
                    <FileText className="w-5 h-5 text-[#4ADE80]" />
                    <div className="flex-1">
                      <p className="text-sm text-white">{documents.bba.name}</p>
                      <p className="text-xs text-gray-500">{(documents.bba.size / 1024).toFixed(0)} KB</p>
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => bbaRef.current?.click()} disabled={!isEditable}
                    className="w-full flex items-center justify-center gap-2 p-4 border border-dashed border-white/20 text-gray-400 hover:text-white hover:border-white/40 transition-colors"
                    data-testid="edit-upload-bba">
                    <Upload className="w-4 h-4" /><span className="text-sm">Upload BBA (PDF)</span>
                  </button>
                )}
                <input ref={bbaRef} type="file" accept=".pdf" className="hidden" onChange={e => handleFileUpload('bba', e.target.files[0])} />
              </div>
              <div className="max-w-sm">
                {renderField('BBA Execution Date', 'bbaDate', { type: 'date', hint: 'Used for appreciation calculations' })}
              </div>
            </div>
          )}

          {/* Agreement Terms */}
          {activeSection === 'agreement' && (
            <div className="space-y-6" data-testid="edit-section-agreement">
              <div>
                <h3 className="text-sm text-white font-medium">Agreement Terms</h3>
                <p className="text-xs text-gray-500 mt-1">These fields populate the LLP agreement sent via Zoho Sign.</p>
              </div>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Minimum Holding Period</label>
                  <select value={formData.holdingPeriod || '6 Months'} onChange={e => update('holdingPeriod', e.target.value)}
                    className={inputCls} disabled={!isEditable} data-testid="edit-holdingPeriod">
                    {['3 Months', '6 Months', '12 Months', '18 Months', '24 Months'].map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <p className="text-xs text-gray-600 mt-1">Partners can exit only after this period + 6 month lock-in</p>
                </div>
                {renderField('Target Appreciation', 'targetAppreciation', { placeholder: '5% annualised increase in Book Value', hint: 'Benchmark from BBA signing date' })}
              </div>
            </div>
          )}

          {/* Payment Schedule */}
          {activeSection === 'payment-plan' && (
            <div className="space-y-6" data-testid="edit-section-payment-plan">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm text-white font-medium">Payment Schedule (Annexure-D)</h3>
                  <p className="text-xs text-gray-500 mt-1">Builder milestone payment schedule. Capital calls derived from this.</p>
                </div>
                <span className={`px-3 py-1 text-sm font-mono ${ppTotal === 100 ? 'bg-[#4ADE80]/20 text-[#4ADE80]' : 'bg-red-500/20 text-red-400'}`}>
                  {ppTotal}% / 100%
                </span>
              </div>
              <div className="space-y-3">
                {paymentPlan.map((slot, idx) => (
                  <div key={idx} className="border border-white/10 p-4 bg-white/[0.02]">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 flex items-center justify-center bg-white/10 text-white text-sm font-mono flex-shrink-0">{idx + 1}</div>
                      <div className="flex-1 grid grid-cols-3 gap-4">
                        <div>
                          <label className="text-xs text-gray-600 mb-1 block">Percentage (%)</label>
                          <input type="number" min="0" max="100" value={slot.percentage}
                            onChange={e => updatePaymentSlot(idx, 'percentage', e.target.value)}
                            disabled={!isEditable} className={`${isEditable ? inputCls : lockedCls} font-mono`} />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 mb-1 block">Milestone *</label>
                          <input value={slot.milestone} onChange={e => updatePaymentSlot(idx, 'milestone', e.target.value)}
                            disabled={!isEditable} className={isEditable ? inputCls : lockedCls} />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 mb-1 block">Description</label>
                          <input value={slot.description || ''} onChange={e => updatePaymentSlot(idx, 'description', e.target.value)}
                            disabled={!isEditable} className={isEditable ? inputCls : lockedCls} placeholder="Optional" />
                        </div>
                      </div>
                      {isEditable && (
                        <button type="button" onClick={() => removePaymentSlot(idx)} className="p-2 text-gray-500 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {isEditable && (
                <button type="button" onClick={addPaymentSlot}
                  className="w-full flex items-center justify-center gap-2 p-3 border border-dashed border-white/20 text-gray-400 hover:text-white hover:border-white/40">
                  <Plus className="w-4 h-4" /> Add Milestone ({paymentPlan.length}/5)
                </button>
              )}
            </div>
          )}

          {/* Edit Reason */}
          {isEditable && (
            <div className="pt-6 mt-6 border-t border-white/10">
              <label className={labelCls}>Reason for Edit <span className="text-red-400">*</span></label>
              <textarea value={editReason} onChange={e => setEditReason(e.target.value)} rows={2}
                placeholder="Enter reason for this change (required for audit trail)..."
                className={`${inputCls} resize-none`} data-testid="edit-reason" />
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Edit History */}
          <div className="pt-4 mt-4 border-t border-white/10">
            <button type="button" onClick={() => setShowHistory(!showHistory)} className="flex items-center gap-2 text-sm text-gray-500 hover:text-white">
              <History className="w-4 h-4" /> Edit History ({history.length})
              {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showHistory && history.length > 0 && (
              <div className="mt-3 space-y-2">
                {history.map((r, i) => (
                  <div key={i} className="p-3 bg-white/5 border border-white/10">
                    <div className="flex justify-between mb-1">
                      <span className="text-xs text-gray-500 font-mono">{new Date(r.timestamp).toLocaleString()}</span>
                      <span className="text-xs text-gray-500">by {r.performedBy}</span>
                    </div>
                    <p className="text-sm text-gray-400">{r.reason}</p>
                    {r.newValues && <p className="text-xs text-gray-600 mt-1">Changed: {Object.keys(r.newValues).join(', ')}</p>}
                  </div>
                ))}
              </div>
            )}
            {showHistory && history.length === 0 && <p className="mt-3 text-sm text-gray-600">No history</p>}
          </div>
        </form>

        {/* Footer */}
        <div className="p-5 border-t border-white/10 flex items-center justify-between flex-shrink-0">
          <div>
            {ppTotal !== 100 && <span className="text-xs text-red-400">Payment Schedule: {ppTotal}%/100%</span>}
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="px-6 py-2.5 border border-white/10 text-white text-sm" data-testid="edit-cancel">Cancel</button>
            {isEditable && (
              <button type="submit" onClick={handleSubmit} disabled={loading || ppTotal !== 100}
                className={`px-6 py-2.5 text-sm font-medium flex items-center gap-2 ${ppTotal === 100 ? 'bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black' : 'bg-gray-700 text-gray-400 cursor-not-allowed'}`}
                data-testid="edit-save">
                <Save className="w-4 h-4" /> {loading ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
